<!-- Responsibility: Implement invitee welcome behavior for the ui/screens surface.
Scope: Invitee landing copy, BYOK guidance, and curated reality launch only. -->
<welcome-view class="menu-overlay invitee-welcome">
  <div class="welcome-shell" aria-label="Invitee alpha welcome">
    <article class="welcome-card">
      <section class="welcome-intro">
        <p class="eyebrow">ALPHA TESTER WELCOME</p>
        <h2>Welcome, and thank you for helping build AnyQuest.</h2>
        <p class="lead">Avi Tshuva appreciates you being among the first people to test a very large system before it opens wider.</p>
        <div class="welcome-copy">
          <p>This is still the alpha surface. We are exposing only a fraction of the system right now, because that fraction is the core of the beast and needs to be perfected first.</p>
          <p>That focus lets us stabilize the core loop before we open the rest: creating and forking your own realities, offering them on a marketplace, joining a creator collective, and eventually letting the system use premium AI models automatically when that makes sense.</p>
          <p>For now, the environment is mostly BYOK. Your keys stay in your browser, games save on the go, and the current emphasis is on rich worlds, behavior, and narrative logic powered by our LLM pipeline.</p>
          <p>We keep the front door narrow on purpose: sign in, set up a provider if you want local AI, then resume or launch a curated reality without hunting through extra screens.</p>
          <p>That is what makes AnyQuest different. It is not just a story generator; it is a system for inhabiting, returning to, and extending the same living reality over time.</p>
        </div>
      </section>

      <div class="welcome-grid">
        <welcome-setup-wizard actions={ { openSetup: openByokSettings } }></welcome-setup-wizard>

        <welcome-reality-selector
          offers={ state.preforgedOffers }
          busy={ state.busyOfferId }
          actions={ { resumeGame: resumeSavedGame, selectOffer: startPreforgedReality } }
        ></welcome-reality-selector>
      </div>

      <p if={ state.maintenanceMode } class="welcome-note">Managed cloud access is paused right now. BYOK and local play remain the recommended path for testers.</p>
      <p if={ state.error } class="welcome-error" aria-live="polite">{ state.error }</p>
    </article>
  </div>

  <script>
    import { dispatcher, navigateState, sysNotify } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { adminStore } from '../stores/admin-store.js'
    import { commerceStore } from '../stores/commerce-store.ts'
    import { formatAIErrorMessage } from '../../ai/errors.js'
    import { launchPreforgedReality } from '../../services/preforge-service.ts'
    import WelcomeRealitySelector from '../components/lobby/welcome-reality-selector.riot'
    import WelcomeSetupWizard from '../components/lobby/welcome-setup-wizard.riot'

    export default {
      components: {
        WelcomeRealitySelector,
        WelcomeSetupWizard,
      },

      state: {
        maintenanceMode: false,
        preforgedOffers: [],
        busyOfferId: '',
        error: '',
      },

      onMounted() {
        this.adminUnsub = adminStore.subscribe(() => this.syncAdminFlags())
        this.commerceUnsub = commerceStore.subscribe(() => this.syncCommerceOffers())
        this.syncAdminFlags()
        this.syncCommerceOffers()
        void commerceStore.refresh()
      },

      onUnmounted() {
        this.adminUnsub?.()
        this.commerceUnsub?.()
      },

      syncAdminFlags() {
        const flags = adminStore.flags
        this.update({ maintenanceMode: flags.maintenanceMode === true })
      },

      syncCommerceOffers() {
        const offers = (commerceStore.preforgedOffers || [])
          .filter((offer) => offer.publishState === 'published' && offer.kind === 'reality')
          .slice(0, 2)
        this.update({ preforgedOffers: offers })
      },

      openByokSettings() {
        dispatcher.trigger('app', TOPICS.ui.dialogOpen, {
          content: 'settings-dialog',
          data: { initialTab: 'advanced' },
          options: { variant: 'notebook' },
        })
      },

      resumeSavedGame() {
        navigateState('saves')
      },

      async startPreforgedReality(offer) {
        if (!offer || this.state.busyOfferId) return
        this.update({ busyOfferId: offer.id, error: '' })
        sysNotify(`Starting ${offer.title}...`)
        try {
          const result = await launchPreforgedReality(offer)
          navigateState('scene', result.saveId)
        } catch (error) {
          const message = `Could not start ${offer.title}: ${formatAIErrorMessage(error).slice(0, 160)}`
          this.update({ error: message })
          sysNotify(message)
        } finally {
          this.update({ busyOfferId: '' })
        }
      },
    }
  </script>

  <style>
    .invitee-welcome {
      width: 100%;
    }

    .welcome-shell {
      display: flex;
      justify-content: center;
      width: 100%;
      min-height: 100dvh;
      padding: var(--space-lg);
    }

    .welcome-card {
      display: grid;
      gap: var(--space-lg);
      width: min(100%, 78rem);
      padding: clamp(var(--space-md), 3vw, var(--space-xl));
      border: 1px solid color-mix(in srgb, var(--border) 52%, transparent);
      border-radius: var(--radius-lg);
      background:
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) 10%, transparent), transparent 24%),
        radial-gradient(circle at left center, color-mix(in srgb, var(--brand-primary) 8%, transparent), transparent 28%),
        linear-gradient(180deg, color-mix(in srgb, #09111a 90%, transparent), color-mix(in srgb, var(--bg) 88%, transparent));
      box-shadow:
        0 2rem 3.5rem rgba(0, 0, 0, 0.42),
        0 0 0 1px rgba(255, 255, 255, 0.02) inset;
    }

    .welcome-intro {
      display: grid;
      gap: var(--space-sm);
      max-width: 58rem;
    }

    .eyebrow {
      margin: 0;
      color: var(--gold);
      font-size: var(--type-xs);
      letter-spacing: 0.18em;
      text-transform: uppercase;
    }

    .welcome-intro h2 {
      margin: 0;
      color: var(--text-prose);
      font-size: clamp(2rem, 3.6vw, 3.3rem);
      line-height: 1.02;
      text-wrap: balance;
    }

    .lead {
      margin: 0;
      max-width: 52rem;
      color: var(--text-dim);
      font-size: var(--type-lg);
      line-height: 1.55;
    }

    .welcome-copy {
      display: grid;
      gap: var(--space-sm);
      max-width: 58rem;
    }

    .welcome-copy p,
    .welcome-note,
    .welcome-error {
      margin: 0;
      font-size: var(--type-base);
      line-height: 1.65;
    }

    .welcome-copy p {
      color: var(--text-dim);
    }

    .welcome-grid {
      display: grid;
      grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
      gap: var(--space-md);
      align-items: start;
    }

    .welcome-note {
      color: var(--gold);
      font-size: var(--type-sm);
    }

    .welcome-error {
      color: var(--danger);
      font-size: var(--type-sm);
    }

    @media (max-width: 62rem) {
      .welcome-shell {
        padding: 0;
      }

      .welcome-card {
        border-radius: 0;
      }

      .welcome-grid {
        grid-template-columns: 1fr;
      }
    }
  </style>
</welcome-view>
